<script setup lang="ts">
import { Upload } from 'lucide-vue-next';
import { computed, onUnmounted, ref, useId, watch } from 'vue';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/composables/useTranslations';
import { cn } from '@/lib/utils';

type Props = {
    accept?: string;
    multiple?: boolean;
    /** Primary line; defaults to translated “Click or drag files here”. */
    label?: string;
    /** Secondary line (e.g. size and format hints), often with middle dots (·). */
    sublabel?: string;
    /** Override the visible upload button label. */
    chooseButtonLabel?: string;
    disabled?: boolean;
    /**
     * When set and `multiple` is false, shows a live preview inside the drop zone.
     * Ignored for bulk (`multiple`) uploads.
     */
    previewFile?: File | null;
};

const props = withDefaults(defineProps<Props>(), {
    accept: '*',
    multiple: false,
    disabled: false,
    previewFile: null,
});

const emit = defineEmits<{
    (e: 'update:modelValue', files: File[]): void;
}>();

const { t } = useTranslations();
const inputId = useId();
const isDraggingOver = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
const previewUrl = ref<string | null>(null);

watch(
    () => props.previewFile,
    (f) => {
        if (previewUrl.value) {
            URL.revokeObjectURL(previewUrl.value);
            previewUrl.value = null;
        }

        if (f && !props.multiple) {
            previewUrl.value = URL.createObjectURL(f);
        }
    },
    { immediate: true },
);

onUnmounted(() => {
    if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value);
    }
});

const showEmbeddedPreview = computed(
    () => !props.multiple && previewUrl.value !== null,
);

const displayLabel = computed(
    () =>
        props.label ??
        t('tools.dropzone.default_label', 'Click or drag files here'),
);

const chooseButtonText = computed(
    () =>
        props.chooseButtonLabel ??
        (props.multiple
            ? t('tools.dropzone.choose_files', 'Choose files')
            : t('tools.dropzone.choose_file', 'Choose file')),
);

const replaceHint = computed(() =>
    t(
        'tools.dropzone.preview_replace',
        'Drop a new file, click here, or use the button below to replace.',
    ),
);

function handleDrop(event: DragEvent): void {
    if (props.disabled) {
        return;
    }

    isDraggingOver.value = false;
    const files = Array.from(event.dataTransfer?.files ?? []);
    emit('update:modelValue', props.multiple ? files : files.slice(0, 1));
}

function handleChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = Array.from(target.files ?? []);
    emit('update:modelValue', files);
    target.value = '';
}

function openFilePicker(): void {
    if (props.disabled) {
        return;
    }

    inputRef.value?.click();
}

function onDragOver(): void {
    if (!props.disabled) {
        isDraggingOver.value = true;
    }
}
</script>

<template>
    <div
        :class="
            cn(
                'touch-manipulation rounded-xl border-2 border-dashed border-slate-300/90 bg-white transition-colors dark:border-slate-600 dark:bg-muted/30',
                isDraggingOver &&
                    'border-brand-500 bg-brand-50/80 dark:border-brand-400 dark:bg-brand-950/50',
                props.disabled && 'pointer-events-none opacity-60',
            )
        "
        @dragover.prevent="onDragOver"
        @dragleave.prevent="isDraggingOver = false"
        @drop.prevent="handleDrop"
    >
        <input
            :id="inputId"
            ref="inputRef"
            type="file"
            class="sr-only"
            :accept="accept"
            :multiple="multiple"
            :disabled="disabled"
            @change="handleChange"
        />

        <!-- With file: thumbnail left; text + choose button stacked on the right -->
        <div
            v-if="showEmbeddedPreview"
            class="flex flex-row items-start gap-4 px-4 py-4 sm:gap-5 sm:px-6 sm:pt-4 sm:pb-5"
        >
            <label
                :for="inputId"
                :class="
                    cn(
                        'relative block h-24 w-24 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-slate-200/90 bg-slate-50/90 sm:h-32 sm:w-32 dark:border-slate-700/90 dark:bg-slate-900/50',
                        props.disabled &&
                            'pointer-events-none cursor-not-allowed',
                    )
                "
            >
                <img
                    :src="previewUrl ?? ''"
                    :alt="previewFile?.name ?? ''"
                    class="h-full w-full object-cover"
                    data-testid="dropzone-embedded-preview"
                />
            </label>
            <div class="flex min-w-0 flex-1 flex-col gap-2 text-left">
                <label
                    :for="inputId"
                    :class="
                        cn(
                            'flex cursor-pointer flex-col gap-1.5',
                            props.disabled &&
                                'pointer-events-none cursor-not-allowed',
                        )
                    "
                >
                    <span
                        class="text-sm font-medium text-pretty text-gray-900 sm:text-base dark:text-gray-100"
                    >
                        {{ replaceHint }}
                    </span>
                    <span
                        v-if="sublabel"
                        class="max-w-xl text-xs leading-relaxed text-pretty text-muted-foreground sm:text-sm"
                    >
                        {{ sublabel }}
                    </span>
                </label>
                <Button
                    type="button"
                    variant="outline"
                    size="default"
                    class="h-9 w-full max-w-xs shadow-xs sm:w-auto sm:min-w-[10.5rem]"
                    :disabled="disabled"
                    @click.prevent="openFilePicker"
                >
                    {{ chooseButtonText }}
                </Button>
            </div>
        </div>

        <!-- Empty: centered stack -->
        <label
            v-else
            :for="inputId"
            :class="
                cn(
                    'flex cursor-pointer flex-col items-center justify-center gap-1 px-3 pt-6 pb-1 text-center sm:px-8 sm:pt-8',
                    props.disabled && 'cursor-not-allowed',
                )
            "
        >
            <Upload
                class="mb-0.5 size-8 shrink-0 text-slate-500 sm:size-9 dark:text-slate-400"
                :stroke-width="1.75"
                aria-hidden="true"
            />
            <span
                class="text-sm font-medium text-gray-900 sm:text-base dark:text-gray-100"
            >
                {{ displayLabel }}
            </span>
            <span
                v-if="sublabel"
                class="max-w-xl text-xs leading-relaxed text-pretty text-gray-500 sm:text-sm dark:text-gray-400"
            >
                {{ sublabel }}
            </span>
        </label>

        <div
            v-if="!showEmbeddedPreview"
            class="flex justify-center px-3 pt-2 pb-6 sm:px-8 sm:pt-3 sm:pb-8"
        >
            <Button
                type="button"
                variant="outline"
                size="default"
                class="h-11 w-full max-w-sm shadow-xs sm:h-9 sm:w-auto sm:min-w-[10.5rem]"
                :disabled="disabled"
                @click.prevent="openFilePicker"
            >
                {{ chooseButtonText }}
            </Button>
        </div>
    </div>
</template>
